import { action, computed, observable } from 'mobx';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import getDataPoint from '../utils/getDataPoint';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { DataLocator } from '../models/configuration/DataLocator';
import getColumnLabel from '../utils/getColumnLabel';
import getColumnLabelFormat from '../utils/getColumnLabelFormat';
import { Stores } from '../models/Stores';
import { DataStore, GlobalDisclaimerStore } from '.';
import {
    isNullOrEmpty,
    isNullOrUndefined,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { Group } from '../models/configuration/explorer/Group';
import { isGUID } from '../utils/isGUID';
import { ConditionalDisclaimer } from '../models/configuration/disclaimers/ConditionalDisclaimer';
import { DisclaimerDisplayType } from '../models/configuration/disclaimers/DisclaimerDisplayType';

export class ColumnStore {
    @observable stores: Stores;

    // Map of column index to selected column group index
    selectedGroupMap = observable.map();

    constructor(stores: Stores) {
        this.stores = stores;
    }

    @computed
    get hasColumnGroupsSet() {
        const { columnGroups } = this.dataStore;
        return !isNullOrEmpty(columnGroups);
    }

    @computed
    get columnGroups() {
        return this.dataStore.columnGroups;
    }

    @computed
    get dataStore(): DataStore {
        return this.stores && this.stores.data;
    }

    // Generate columns for UI - adds disclaimers, dropdown options, and dynamic columns
    @computed
    get columns(): Array<ColumnConfig> {
        const processedColumns = this.dataStore.config.columnConfig.map(
            (column, columnIndex) => {
                return this.processColumn(column, columnIndex);
            }
        );

        return processedColumns;
    }

    processColumn = (column: any, columnIndex: number): ColumnConfig => {
        const {
            key,
            type,
            groups = [],
            columns = [],
            disclaimerTypes = [],
        } = column as any;
        const label = getColumnLabel(column, this.dataStore);

        if (type === 'dropdown' && groups.length > 0) {
            return {
                key,
                type: 'dropdown',
                groups: groups.map(g => ({
                    ...g,
                    type: 'dropdown',
                    columnIndex,
                    options: groups.map((c, i) => ({
                        groupIndex: i,
                        label: c.label,
                    })),
                    columns:
                        g.columns && g.columns.length > 0
                            ? [].concat(
                                  ...g.columns.map(c => {
                                      if (c.type === 'dynamic') {
                                          return this.getColumnsFromDataSet(c);
                                      }

                                      return {
                                          ...c,
                                          disclaimers: GlobalDisclaimerStore.getSome(
                                              c.disclaimerTypes
                                          ),
                                      };
                                  })
                              )
                            : g.type === 'dynamic'
                            ? this.getColumnsFromDataSet(g)
                            : [
                                  {
                                      ...g,
                                      disclaimers: GlobalDisclaimerStore.getSome(
                                          g.disclaimerTypes
                                      ),
                                  },
                              ],
                    disclaimers: GlobalDisclaimerStore.getSome(
                        g.disclaimerTypes
                    ),
                    mappedDisclaimers: g.disclaimerTypes?.map(disclaimer => {
                        const temp: ConditionalDisclaimer = {
                            display: DisclaimerDisplayType.tooltip,
                            disclaimerType: disclaimer,
                        };
                        return temp;
                    }),
                })),
            };
        }

        // Handle group array of columns
        if (type === 'group' && columns.length > 0) {
            return {
                ...column,
                // used for expanded colspan
                total: columns.length,
                columns: [].concat(
                    ...columns.map(c => {
                        if (c.type === 'dynamic') {
                            return this.getColumnsFromDataSet(c);
                        }

                        return {
                            ...c,
                            disclaimers: GlobalDisclaimerStore.getSome(
                                c.disclaimerTypes
                            ),
                        };
                    })
                ),
                disclaimers: GlobalDisclaimerStore.getSome(disclaimerTypes),
            };
        }

        // Handle group dynamic columns
        if (type === 'group' && columns.type === 'dynamic') {
            const dataSetColumns = this.getColumnsFromDataSet(columns);
            return {
                ...column,
                // used for expanded colspan
                total: dataSetColumns.length,
                label,
                columns: dataSetColumns,
                disclaimers: GlobalDisclaimerStore.getSome(disclaimerTypes),
            };
        }

        return {
            ...column,
            // used for expanded colspan
            total: 1,
            label,
            disclaimers: GlobalDisclaimerStore.getSome(disclaimerTypes),
        };
    };

    // Generate columns for data set
    getColumnsFromDataSet = (column: ColumnConfig): Array<ColumnConfig> => {
        const { data, disclaimerTypes } = column;
        const label = getColumnLabel(column, this.dataStore);
        const labelFormat = getColumnLabelFormat(column, this.dataStore);

        // Add locale to label format
        const format = {
            ...labelFormat,
            locale: this.dataStore.culture,
        };

        // Get a dataSet with maximum length of values to use for populating columns
        const columnsDataSet = this.dataStore.funds.reduce((acc, fund) => {
            const dataSet = getDataPoint({
                dataStore: this.dataStore,
                dataConfig: data as DataLocator,
                shareClass: fund,
            });

            if (dataSet.length > acc.length) {
                return dataSet;
            }

            return acc;
        }, []);

        // Create an array of columns for length of columnsDataSet
        // Any placeholders used in column labels will be replaced using values from columnsDataSet
        // The data locator for the column is given the index of the current column
        if (Array.isArray(columnsDataSet)) {
            return columnsDataSet.map((values, i) => {
                return {
                    ...column,
                    label: replacePlaceholders({
                        values,
                        format,
                        text: label,
                        dataStore: this.dataStore,
                    }),
                    data: {
                        ...data,
                        sortKey: values.label,
                        index: i,
                    },
                    disclaimers: GlobalDisclaimerStore.getSome(disclaimerTypes),
                };
            });
        }

        return [];
    };

    // Sortable columns array for use in sort selector dropdown
    @computed
    get sortableColumns(): Array<ColumnConfig> {
        const sortableColumns = [];
        this.dataStore.config.columnConfig.map(c => {
            const { label, sortable, columns, groups, data } = c as any;

            if (sortable) {
                sortableColumns.push({
                    ...c,
                });
            }

            if (data && data.length > 0) {
                data.map(cc => {
                    if (cc.sortable) {
                        sortableColumns.push({
                            ...cc,
                            property: cc.code,
                            label: `${label}`,
                        });
                    }
                });
            }

            if (columns) {
                columns.map(cc => {
                    if (cc.sortable) {
                        sortableColumns.push({
                            ...cc,
                            label: `${label}: ${cc.label}`,
                        });
                    }
                });
            }

            if (groups) {
                groups.map(g => {
                    if (g.columns) {
                        g.columns.map(gc => {
                            if (gc.sortable) {
                                sortableColumns.push({
                                    ...gc,
                                    label: `${g.label}: ${gc.label}`,
                                });
                            }
                        });
                    } else {
                        sortableColumns.push({
                            ...g,
                            label: `${g.label}`,
                        });
                    }
                });
            }
        });

        return sortableColumns;
    }

    // Sets the selected group of dropdown columns
    @action
    setSelectedGroup = (columnIndex: string, groupIndex: number) => {
        this.selectedGroupMap.set(columnIndex, groupIndex);
    };

    getSelectedGroup = (columnKey: string) => {
        const columnIndex = this.columns.findIndex(c => c.key === columnKey);
        let groupIndex = this.selectedGroupMap.get(columnIndex);
        const column = this.columns[columnIndex];
        if (column && column.groups) {
            if (typeof groupIndex === 'number') {
                if (groupIndex >= column.groups.length) {
                    const selectedConfigGroupIndex = column.groups.findIndex(
                        c => (c as any).selected
                    );
                    // tslint:disable-next-line: prefer-conditional-expression
                    if (selectedConfigGroupIndex > -1) {
                        groupIndex = selectedConfigGroupIndex;
                    } else {
                        groupIndex = 0;
                    }
                }
                return this.columns[columnIndex].groups[groupIndex];
            }
        }
    };

    @action
    setInitialGroupIndex = (columnIndex: number, column: ColumnConfig) => {
        let selectedGroupIndex;
        if (this.hasColumnGroupsSet) {
            const columnGroups = this.columnGroups;
            const configColumnKey = column.key;
            const columnGroupSelectedValue = !isGUID(configColumnKey)
                ? columnGroups[configColumnKey]
                : columnGroups[columnIndex];
            if (isNaN(Number(columnGroupSelectedValue))) {
                selectedGroupIndex = column.groups.findIndex(
                    group => group.key === columnGroupSelectedValue
                );
            } else {
                selectedGroupIndex = Number(columnGroupSelectedValue);
            }
        }

        if (
            isNullOrUndefined(selectedGroupIndex) ||
            selectedGroupIndex === -1
        ) {
            const selectedConfigGroupIndex = column.groups.findIndex(
                c => c.selected
            );

            const index = this.getSelectedGroupIndex(
                columnIndex,
                column.groups
            );

            selectedGroupIndex = index
                ? index
                : selectedConfigGroupIndex > -1
                ? selectedConfigGroupIndex
                : 0;
        }

        this.selectedGroupMap.set(columnIndex, selectedGroupIndex);
    };

    getSelectedGroupIndex(columnIndex: number, groups: Array<Group>) {
        let index = this.selectedGroupMap.get(columnIndex);

        if (groups) {
            if (index >= groups.length) {
                const selectedConfigGroupIndex = groups.findIndex(
                    c => c.selected
                );
                // tslint:disable-next-line: prefer-conditional-expression
                if (selectedConfigGroupIndex > -1) {
                    index = selectedConfigGroupIndex;
                } else {
                    index = 0;
                }
            }
        }

        return index;
    }
}



// WEBPACK FOOTER //
// ./src/stores/ColumnStore.ts